import type { ReactNode } from 'react';
import { BookOpen, HelpCircle } from 'lucide-react';
import { useState } from 'react';
import { useSimpleLanguage } from '../hooks/useSimpleLanguage';

interface LoginLayoutProps {
  children: ReactNode;
}

const LoginLayout = ({ children }: LoginLayoutProps) => {
  // Use lightweight language hook (no Redux) for login page optimization
  const { t, isRTL } = useSimpleLanguage('login');
  const [showDemoCredentials, setShowDemoCredentials] = useState(false);
  return (
    <div className={`min-h-screen flex ${isRTL ? 'flex-row-reverse' : ''}`}>
      {/* Left Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-black py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Decorative blur circles */}
        <div className="absolute top-20 left-20 w-64 h-64 bg-sky-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
        
        <div className="max-w-md w-full space-y-4 relative z-10">
          {/* Main Content */}
          <div className="py-8 px-6">
            {children}
          </div>

          {/* Demo Credentials Button */}
          <div className="px-6 pb-4 flex justify-center">
            <button
              onClick={() => setShowDemoCredentials(!showDemoCredentials)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-400 hover:text-blue-400 transition-colors bg-slate-800/30 hover:bg-slate-800/50 rounded-full border border-slate-700/50 hover:border-blue-500/30 backdrop-blur-sm"
            >
              <HelpCircle size={14} />
              Demo Accounts
            </button>
          </div>

          {/* Demo Credentials Popup */}
          {showDemoCredentials && (
            <div className="px-6 pb-4 space-y-2 animate-in fade-in duration-200">
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-3 border border-slate-700/50 hover:border-blue-500/30 transition-colors">
                <div className="text-xs font-medium text-white mb-1">Owner</div>
                <div className="text-xs text-gray-300 font-mono space-y-0.5">
                  <div>Email:demo.owner@educenter.af</div>
                  <div>Password: demo123</div>
                  
                </div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-3 border border-slate-700/50 hover:border-blue-500/30 transition-colors">
                <div className="text-xs font-medium text-white mb-1">Branch Manager</div>
                <div className="text-xs text-gray-300 font-mono space-y-0.5">
                  <div>Email:demo.manager@educenter.af</div>
                  <div>Password: demo123</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right Side - Background Image */}
      <div className={`hidden lg:block w-1/2 relative overflow-hidden bg-sky-950 ${isRTL ? 'order-first' : ''}`}>
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{animationDelay: '2s'}}></div>
        
        <div className="h-full flex flex-col items-center justify-center px-8 relative z-10">
          {/* Main Content */}
          <div className="text-center mb-12">
            {/* Books Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 backdrop-blur-md bg-blue-500/20 rounded-full flex items-center justify-center border border-blue-400/50 shadow-xl">
                <BookOpen className="h-10 w-10 text-blue-300" />
              </div>
            </div>
            <h3 className="text-4xl font-bold text-white drop-shadow-lg mb-4">{t('appName')}</h3>
            <p className="text-xl text-blue-100 drop-shadow-lg font-light">{t('appSubtitle')}</p>
          </div>

          {/* Features Grid */}
          <div className="space-y-4 w-full max-w-sm">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-400 flex items-center justify-center">
                <span className="text-white font-bold text-sm">✓</span>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-white">{t('comprehensiveManagement')}</h4>
                <p className="text-xs text-blue-200">{t('comprehensiveDesc')}</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-400 flex items-center justify-center">
                <span className="text-white font-bold text-sm">✓</span>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-white">{t('realTimeAnalytics')}</h4>
                <p className="text-xs text-blue-200">{t('analyticsDesc')}</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-400 flex items-center justify-center">
                <span className="text-white font-bold text-sm">✓</span>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-white">{t('secureReliable')}</h4>
                <p className="text-xs text-blue-200">{t('secureDesc')}</p>
              </div>
            </div>
          </div>

          {/* Footer Text */}
          <div className="mt-12 text-center">
            <p className="text-xs text-blue-300">{t('empowering')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enable why-did-you-render tracking for this component
(LoginLayout as any).whyDidYouRender = true;

export default LoginLayout;
